// OTA 1.0.3 (2026-06-19) — Shared auth-diagnostic helpers used by
// PremiumAuthPage.jsx and EmailAuth.jsx.  Single source of truth for
// the sanitized-envelope serializer + the native-safe clipboard copy.
//
// SANITIZATION INVARIANT — what this module emits MUST NEVER include:
//   - password (never read into errorDetails)
//   - session_token / api token (diag block fires only on FAILURE)
//   - Authorization header
//   - Set-Cookie value
//   - full request URL with query strings
//   - PII beyond the email already echoed in the form input
// Allow-listed fields only.

/**
 * Serialize the diagnostic envelope into a plain-text block ready to
 * paste into support chat / email / GitHub issue.  Each field on its
 * own line for maximum readability across SMS, Discord, Slack, etc.
 * Skips falsy fields so the paste isn't padded with "—".
 *
 * @param {object} details - errorDetails object from state
 * @returns {string} multi-line plain-text block
 */
export function serializeAuthDiagnostic(details) {
  if (!details || typeof details !== "object") return "";
  const order = [
    ["status", details.status],
    ["endpoint", details.endpoint],
    ["backend", details.backendHost],
    ["code", details.code],
    ["name", details.name],
    ["detail", details.detail],
    ["req-id", details.requestId],
    ["ts", details.ts],
  ];
  return order
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${k}: ${v}`)
    .join("\n");
}

/**
 * Native-safe clipboard copy.  Prefers the modern async clipboard API,
 * falls back to the deprecated execCommand("copy") path for older
 * Capacitor WebView builds where navigator.clipboard may be undefined
 * or blocked by a missing permission policy.
 *
 * @param {string} text - text to copy (already sanitized)
 * @returns {Promise<boolean>} true on success, false on failure
 */
export async function copyAuthDiagnostic(text) {
  if (!text) return false;
  // Path A: modern async clipboard (most Capacitor + browser builds).
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { void e; /* fall through to execCommand path */ }
  // Path B: legacy execCommand fallback.  Works in older Android
  // WebViews that don't expose navigator.clipboard.
  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.left = "-9999px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (e) {
    void e;
    return false;
  }
}
